import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Form,
  Tooltip,
} from "@bleu.builders/ui";
import {
  BellIcon,
  CircleIcon,
  FileDownIcon,
  Newspaper,
  Wrench,
} from "lucide-react";
import React from "react";
import useSWR, { mutate } from "swr";

import { notifications } from "#/api/all";
import { useUser } from "#/contexts/UserContext";
import { formatDate } from "#/lib/formatDate";
import { cn } from "#/lib/utils";
import { client } from "#/utils/api-client";

const NOTIFICATION_ICONS = {
  app_update_notifier: (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#C69500] text-primary-foreground">
      <Wrench className="size-5" />
    </div>
  ),
  thesis_update_notifier: (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#046865] text-primary-foreground">
      <FileDownIcon className="size-5" />
    </div>
  ),
  news_article_notifier: (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
      <Newspaper className="size-5" />
    </div>
  ),
};

function NotificationItem({
  id,
  title,
  description,
  time,
  event_type,
  url,
  read_at,
  seen_at,
  user_id,
}) {
  // mark as read and redirect
  const onNotificationClick = async () => {
    if (read_at) {
      window.location.href = url;
      return;
    }

    await client(notifications.markAsRead.path({ user_id, id }), {
      method: "POST",
    });

    window.location.href = url;
  };

  return (
    <button onClick={onNotificationClick} className="w-full text-left">
      <div className="flex items-start gap-4 hover:bg-muted rounded-md py-1">
        {NOTIFICATION_ICONS[event_type]}
        <div className="space-y-1 w-full">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium w-11/12">{title}</p>
            <Tooltip
              className="flex items-center"
              content={seen_at ? `Lida em ${formatDate(seen_at)}.` : "Não lida"}
            >
              <CircleIcon
                className={cn("size-3 text-muted-foreground mr-2", {
                  "fill-primary": seen_at,
                })}
              />
            </Tooltip>
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
          <p className="text-xs text-muted-foreground">{formatDate(time)}</p>
        </div>
      </div>
    </button>
  );
}

export function Notifications() {
  const { user } = useUser();

  const { data, error, isLoading } = useSWR(
    [notifications.index.path({ user_id: user.id }), {}],
    async ([url]) => await client(url),
    {
      keepPreviousData: true,
      // refreshInterval: 1000 * 60 * 60, // 1 hour
    },
  );

  const reloadData = () => {
    setTimeout(() => {
      mutate([notifications.index.path({ user_id: user.id }), {}]);
    }, 1000);
  };

  const notSeen = data?.data?.filter(
    (notification) => !notification.seen_at,
  )?.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <BellIcon className="size-6" />
          <div className="absolute top-0 right-0 flex size-4 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
            {notSeen}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[400px] p-0">
        <Card className="border-0 shadow-lg">
          <CardHeader className="border-b px-5 py-3">
            <CardTitle>Notificações</CardTitle>
            <CardDescription>
              Voce tem {notSeen} notificações não vistas.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading && (
              <div className="text-center text-muted-foreground py-5">
                Carregando...
              </div>
            )}
            {error && (
              <div className="text-center text-muted-foreground py-5">
                Erro ao carregar
              </div>
            )}
            {data?.data && data.data?.length === 0 && (
              <div className="text-center text-muted-foreground py-5">
                Sem notificações
              </div>
            )}
            {data?.data && data?.data?.length > 0 && (
              <div className="space-y-4 p-4 overflow-y-auto max-h-80">
                {data.data.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    id={notification.id}
                    user_id={user.id}
                    title={notification.title}
                    description={notification.description}
                    time={notification.created_at}
                    event_type={notification.event_type}
                    url={notification.url}
                    seen_at={notification.seen_at}
                    read_at={notification.read_at}
                  />
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="border-t px-5 py-2">
            <Form
              action={notifications.readAll.path({ user_id: user.id })}
              method="post"
              className="w-full flex justify-center"
            >
              <Button variant="link" type="submit" onClick={reloadData}>
                Marcar todas como lidas
              </Button>
            </Form>
          </CardFooter>
        </Card>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
